import React, { useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import axios from "axios";
import { baseurladdress } from "../baseurl";
import Moment from "moment";
import Swal from "sweetalert2";

function EditAppoint() {
    const { appoint_id } = useParams();
    const navigate = useNavigate();
    const accesstoken = localStorage.getItem("token");
    const accessuserid = localStorage.getItem("id");
    const username = localStorage.getItem("username");
    const [staff, setStaff] = useState("");
    const [staffName, setStaffName] = useState("");
    const [clientType, setClientType] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientName, setClientName] = useState("");
    const [appointmentDate, setAppointmentDate] = useState("");
    const [timeFrom, setTimeFrom] = useState("");
    const [timeTo, setTimeTo] = useState("");
    const [overrideAssignment, setOverrideAssignment] = useState(false);

    const [staffList, setStaffList] = useState([]);
    const [clientList, setClientList] = useState([]);
    const [appointmentData, setAppointmentData] = useState(null);
    const [initialClientId, setInitialClientId] = useState("");

    // Fetch all staff
    useEffect(() => {
        axios
            .get(baseurladdress + "staff/findallstaff", {
                headers: { Authorization: `Bearer ${accesstoken}` },
            })
            .then((res) => {
                if (res.data.status === "Success") {
                    setStaffList(res.data.staff);
                } else {
                    setStaffList([]);
                }
            })
            .catch(() => {
                alert("Failed to load staff list");
            });
    }, [accesstoken]);

    // Fetch appointment details
    useEffect(() => {
        axios
            .get(`${baseurladdress}appointment/editappointment/${appoint_id}`, {
                headers: { Authorization: `Bearer ${accesstoken}` },
            })
            .then((res) => {
                if (res.data.status === "success" && res.data.response.length > 0) {
                    const data = res.data.response[0];
                    setAppointmentData(data);
                    setStaff(data.staff_id || "");
                    setStaffName(data.staff_name || "");
                    setClientType(data.client_type !== undefined ? data.client_type : "");
                    setClientId(data.client_id || "");
                    setClientName(data.client_name || "");
                    setInitialClientId(data.client_id || "");
                    setAppointmentDate(data.appointment_date ? Moment(data.appointment_date).format("YYYY-MM-DD") : "");
                    setTimeFrom(data.time_from || "");
                    setTimeTo(data.time_to || "");
                    setOverrideAssignment(data.is_override || false);
                }
            })
            .catch(() => { });
    }, [appoint_id, accesstoken]);

    // Fetch clients (doctors, hospitals, or clinics) by staff
    useEffect(() => {
        if (!staff || clientType === "") {
            setClientList([]);
            return;
        }

        const fetchClients = async () => {
            try {
                // Handle Doctors (clientType = 0)
                if (clientType === 0) {
                    const endpoint = overrideAssignment
                        ? `${baseurladdress}doctors/findalldoctors`
                        : `${baseurladdress}appointment/bystaff/${staff}`;

                    const res = await axios.get(endpoint, {
                        headers: { Authorization: `Bearer ${accesstoken}` },
                    });

                    if (res.data.status === "Success") {
                        let doctors = res.data.doctors;

                        // Check if initial doctor exists in the list
                        if (!overrideAssignment && initialClientId) {
                            const doctorExists = doctors.some(d => d.doctor_id == initialClientId);

                            if (!doctorExists) {
                                try {
                                    const allDoctorsRes = await axios.get(
                                        `${baseurladdress}doctors/findalldoctors`,
                                        { headers: { Authorization: `Bearer ${accesstoken}` } }
                                    );

                                    if (allDoctorsRes.data.status === "Success") {
                                        const initialDoctor = allDoctorsRes.data.doctors.find(
                                            d => d.doctor_id == initialClientId
                                        );

                                        if (initialDoctor) {
                                            doctors = [
                                                {
                                                    ...initialDoctor,
                                                    isOverride: true
                                                },
                                                ...doctors
                                            ];
                                        }
                                    }
                                } catch (err) {
                                    console.error("Error fetching initial doctor:", err);
                                }
                            }
                        }

                        setClientList(doctors.map(d => ({
                            id: d.doctor_id,
                            name: d.doctor_name,
                            isOverride: d.isOverride || false
                        })));
                    } else {
                        setClientList([]);
                    }
                }
                // Handle Hospitals (clientType = 1)
                else if (clientType === 1) {
                    const endpoint = overrideAssignment
                        ? `${baseurladdress}hospital/findallhospitals`
                        : `${baseurladdress}appointment/hospitalbystaff/${staff}`;

                    const res = await axios.get(endpoint, {
                        headers: { Authorization: `Bearer ${accesstoken}` }
                    });

                    if (res.data.status === "Success") {
                        let hospitals = res.data.hospitals;

                        // Check if initial hospital exists in the list
                        if (!overrideAssignment && initialClientId) {
                            const hospitalExists = hospitals.some(h => h.hosp_id == initialClientId);

                            if (!hospitalExists) {
                                try {
                                    const allHospitalsRes = await axios.get(
                                        `${baseurladdress}hospital/findallhospitals`,
                                        { headers: { Authorization: `Bearer ${accesstoken}` } }
                                    );

                                    if (allHospitalsRes.data.status === "Success") {
                                        const initialHospital = allHospitalsRes.data.hospitals.find(
                                            h => h.hosp_id == initialClientId
                                        );

                                        if (initialHospital) {
                                            hospitals = [
                                                {
                                                    ...initialHospital,
                                                    isOverride: true
                                                },
                                                ...hospitals
                                            ];
                                        }
                                    }
                                } catch (err) {
                                    console.error("Error fetching initial hospital:", err);
                                }
                            }
                        }

                        setClientList(hospitals.map(h => ({
                            id: h.hosp_id,
                            name: h.hosp_name,
                            isOverride: h.isOverride || false
                        })));
                    } else {
                        setClientList([]);
                    }
                }
                // Handle Clinics (clientType = 2)
                else if (clientType === 2) {
                    const endpoint = overrideAssignment
                        ? `${baseurladdress}clinic/findallclinics`
                        : `${baseurladdress}appointment/clinicbystaff/${staff}`;

                    const res = await axios.get(endpoint, {
                        headers: { Authorization: `Bearer ${accesstoken}` }
                    });

                    if (res.data.status === "Success") {
                        let clinics = res.data.clinics;

                        // Check if initial clinic exists in the list
                        if (!overrideAssignment && initialClientId) {
                            const clinicExists = clinics.some(c => c.clinic_id == initialClientId);

                            if (!clinicExists) {
                                try {
                                    const allClinicsRes = await axios.get(
                                        `${baseurladdress}clinic/findallclinics`,
                                        { headers: { Authorization: `Bearer ${accesstoken}` } }
                                    );

                                    if (allClinicsRes.data.status === "Success") {
                                        const initialClinic = allClinicsRes.data.clinics.find(
                                            c => c.clinic_id == initialClientId
                                        );

                                        if (initialClinic) {
                                            clinics = [
                                                {
                                                    ...initialClinic,
                                                    isOverride: true
                                                },
                                                ...clinics
                                            ];
                                        }
                                    }
                                } catch (err) {
                                    console.error("Error fetching initial clinic:", err);
                                }
                            }
                        }

                        setClientList(clinics.map(c => ({
                            id: c.clinic_id,
                            name: c.clinic_name,
                            isOverride: c.isOverride || false
                        })));
                    } else {
                        setClientList([]);
                    }
                }

            } catch (err) {
                console.error(err);
                setClientList([]);
            }
        };

        fetchClients();
    }, [clientType, staff, overrideAssignment, accesstoken, initialClientId]);

    const handleOverrideChange = (e) => {
        setOverrideAssignment(e.target.checked);
        if (!e.target.checked && clientId !== initialClientId) {
            setClientId("");
            setClientName("");
        }
    };

    const getClientTypeName = () => {
        switch (clientType) {
            case 0: return "doctors";
            case 1: return "hospitals";
            case 2: return "clinics";
            default: return "clients";
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        const data = {
            appoint_id: appoint_id,
            staff_id: staff,
            staff_name: staffName,
            client_type: clientType,
            client_id: clientId,
            client_name: clientName,
            appointment_date: appointmentDate,
            time_from: timeFrom,
            time_to: timeTo,
            assigned_userid: accessuserid,
            assigned_staffname: username,
            updated_by: accessuserid,
            is_override: overrideAssignment
        };

        fetch(baseurladdress + "appointment/updateappointment", {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${accesstoken}`,
            },
            body: JSON.stringify(data),
        })
            .then((res) => res.json())
            .then((result) => {
                const Toast = Swal.mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 1800,
                    timerProgressBar: true,
                });

                if (result.status === "updated" || result.status === "Success") {
                    Toast.fire({
                        icon: "success",
                        title: "Appointment updated successfully",
                    });
                    navigate("/appointments");
                } else {
                    Toast.fire({
                        icon: "error",
                        title: "Something went wrong",
                    });
                }
            })
            .catch((error) => {
                Swal.fire({
                    icon: "error",
                    title: "Server Error",
                    text: error.message,
                });
            });
    };

    return (
        <div className="staff-page">
            <div className="staff-container">
                <div className="staff-header">
                    <NavLink to="/appointments">
                        <div className="float">
                            <i className="fa fa-arrow-left my-float"></i>
                        </div>
                    </NavLink>
                </div>

                <form onSubmit={handleSubmit} className="staff-form">
                    <div className="staff-columns">
                        <div className="staff-column">
                            <div className="staff-card">
                                <div className="staff-card-body">
                                    <div className="card-header">
                                        <h4 className="card-title">EDIT APPOINTMENT</h4>
                                        <div className="staff-heading-divider"></div>
                                    </div>

                                    <div className="staff-fields">
                                        <div className="staff-group">
                                            <label>Staff <span className="staff-required">*</span></label>
                                            <select
                                                value={staff}
                                                onChange={(e) => {
                                                    setStaff(e.target.value);
                                                    const selectedStaff = staffList.find(s => s.staff_id == e.target.value);
                                                    setStaffName(selectedStaff?.staff_name || "");
                                                    setClientId("");
                                                    setClientName("");
                                                }}
                                            >
                                                <option value="">Select</option>
                                                {staffList.map((item) => (
                                                    <option key={item.staff_id} value={item.staff_id}>
                                                        {item.staff_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="staff-group">
                                            <label>Client Type <span className="staff-required">*</span></label>
                                            <select
                                                value={clientType}
                                                onChange={(e) => {
                                                    setClientType(Number(e.target.value));
                                                    setClientId("");
                                                    setClientName("");
                                                }}
                                                disabled={!staff}
                                            >
                                                <option value="">Select</option>
                                                <option value={2}>Clinics</option>
                                                <option value={1}>Hospital</option>
                                                <option value={0}>Doctor</option>
                                            </select>
                                        </div>

                                        {/* Override Checkbox - Show for all client types */}
                                        {(clientType === 0 || clientType === 1 || clientType === 2) && (
                                            <div className="staff-group">
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={overrideAssignment}
                                                        onChange={handleOverrideChange}
                                                        style={{ width: 'auto', cursor: 'pointer' }}
                                                    />
                                                    <span>Emergency assignment (Show all {getClientTypeName()})</span>
                                                </label>
                                                {overrideAssignment && (
                                                    <small style={{ color: '#ff9800', display: 'block', marginTop: '4px' }}>
                                                        ⚠️ Emergency/Special case - All {getClientTypeName()} are now available
                                                    </small>
                                                )}
                                            </div>
                                        )}

                                        <div className="staff-group">
                                            <label>Client Name <span className="staff-required">*</span></label>
                                            <select
                                                value={clientId}
                                                onChange={(e) => {
                                                    setClientId(e.target.value);
                                                    const selectedClient = clientList.find(c => c.id == e.target.value);
                                                    setClientName(selectedClient?.name || "");
                                                }}
                                                disabled={clientType === "" || clientType === null}
                                            >
                                                <option value="">Select</option>
                                                {clientList.map((item) => (
                                                    <option
                                                        key={item.id}
                                                        value={item.id}
                                                        style={item.isOverride ? {
                                                            backgroundColor: '#fff3cd',
                                                            fontWeight: 'bold'
                                                        } : {}}
                                                    >
                                                        {item.name}
                                                        {item.isOverride && ' ⚠️ '}
                                                    </option>
                                                ))}
                                            </select>
                                            {clientList.find(c => c.id == clientId)?.isOverride && (
                                                <small style={{ color: '#ff9800', display: 'block', marginTop: '4px' }}>
                                                    This {clientType === 0 ? 'doctor' : clientType === 1 ? 'hospital' : 'clinic'} was assigned via emergency override
                                                </small>
                                            )}
                                        </div>

                                        <div className="staff-group">
                                            <label>Appointment Date <span className="staff-required">*</span></label>
                                            <input
                                                type="date"
                                                value={appointmentDate}
                                                onChange={(e) => setAppointmentDate(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="staff-column">
                            <div className="staff-card">
                                <div className="staff-card-body">
                                    <div className="staff-fields">
                                        <div className="staff-group">
                                            <label>Time From <span className="staff-required">*</span></label>
                                            <input
                                                type="time"
                                                value={timeFrom}
                                                onChange={(e) => setTimeFrom(e.target.value)}
                                            />
                                        </div>

                                        <div className="staff-group">
                                            <label>Time To <span className="staff-required">*</span></label>
                                            <input
                                                type="time"
                                                value={timeTo}
                                                onChange={(e) => setTimeTo(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="staff-btn-container">
                        <button type="submit" className="staff-btn">Update</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditAppoint;